import { Loader } from "@package/subframe/components/Loader";
import { Select } from "@package/subframe/components/Select";
import { FeatherChevronRight } from "@subframe/core";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { isLaunchedEventType } from "@/components/event-records/event-record-schema";
import { useNursery } from "@/contexts/NurseryContext";
import { useListEventRecordsQueryControllerList } from "@/generated/event-records/event-records";
import { useListEventTypesQueryControllerList } from "@/generated/event-types/event-types";
import type { EventRecordUserSummaryDto } from "@/generated/model";
import { eventRecordPill } from "@/lib/workspace/status-pill";

interface EventRecordsTabProps {
  startDate: Date;
  endDate: Date;
}

export function EventRecordsTab({ startDate, endDate }: EventRecordsTabProps) {
  const { selectedNurseryId } = useNursery();
  const nurseryId = selectedNurseryId ?? "";
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("");

  const { data: eventTypesResponse } = useListEventTypesQueryControllerList(
    nurseryId,
    {
      query: { enabled: !!nurseryId },
    },
  );
  const eventTypes = (eventTypesResponse?.data?.items ?? []).filter((t) =>
    isLaunchedEventType(t.slug),
  );

  const { data: recordsResponse, isLoading } =
    useListEventRecordsQueryControllerList(
      nurseryId,
      {
        eventTypeId: eventTypeFilter || undefined,
        from: startDate.toISOString(),
        to: endDate.toISOString(),
      },
      { query: { enabled: !!nurseryId, refetchOnMount: "always" } },
    );
  // No `tab` is passed: this list shows every status, unlike the Events Hub.
  const records = recordsResponse?.data?.data ?? [];

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Filters */}
      <div className="flex w-full flex-wrap items-end gap-3">
        <Select
          label="Event type"
          placeholder="All event types"
          value={eventTypeFilter || "all"}
          onValueChange={(v) => setEventTypeFilter(v === "all" ? "" : v)}
        >
          <Select.Item value="all">All event types</Select.Item>
          {eventTypes.map((et) => (
            <Select.Item key={et.id} value={et.id}>
              {et.name}
            </Select.Item>
          ))}
        </Select>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : records.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-12">
          <span className="text-body font-body text-subtext-color">
            No event records found for this period
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-2">
          {records.map((record) => (
            <Link
              key={record.id}
              to="/event-records/$eventRecordId"
              params={{ eventRecordId: record.id }}
              className="w-full"
            >
              <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3 hover:bg-neutral-50 transition-colors cursor-pointer">
                <div className="flex grow flex-col items-start gap-0.5">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {record.eventTypeName}
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    {format(new Date(record.submittedAt), "d MMM yyyy, HH:mm")}
                    {record.createdBy && (
                      <> &middot; {userLabel(record.createdBy)}</>
                    )}
                  </span>
                </div>
                {/* The lifecycle state, not the old acknowledged-yes/no boolean:
                    this list already received `status` and ignored it, so a
                    resolved or archived record read "Acknowledged" here while the
                    Events Hub read something else (NAU-342). */}
                <WorkspaceStatusBadge pill={eventRecordPill(record.status)} />
                <FeatherChevronRight className="h-4 w-4 text-subtext-color" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function userLabel(u: EventRecordUserSummaryDto): string {
  return [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email;
}
